import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Section } from "@/components/site/Section";
import { CTAButton, mailto } from "@/components/site/CTAButton";
import { ConsultationWizard } from "@/components/site/ConsultationWizard";
import portrait from "@/assets/karel-bauer-new.jpg.asset.json";

const LINKEDIN_URL = "https://www.linkedin.com/in/karelbauer/";
const EPO_OJ_URL = "https://www.epo.org/en/legal/official-journal/2022/09/a89";

export const Route = createFileRoute("/services/european-patent-attorney")({
  head: () => ({
    meta: [
      { title: "European Patent Attorney for US Companies" },
      {
        name: "description",
        content:
          "European Patent Attorney for US companies with 50+ EPO prosecution matters, 10+ years of practice and technical focus in physics, electronics and ICT.",
      },
      {
        property: "og:title",
        content: "European Patent Attorney for US Companies",
      },
      { property: "og:image", content: "https://bauerip.com/og-us.jpg" },
      { name: "twitter:image", content: "https://bauerip.com/og-us.jpg" },
      {
        property: "og:description",
        content:
          "Direct European patent prosecution counsel for US companies: 50+ EPO matters, 10+ years of practice and precisely stated UPC qualifications.",
      },

      {
        property: "og:url",
        content: "https://bauerip.com/services/european-patent-attorney",
      },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary" },
    ],

    links: [
      {
        rel: "canonical",
        href: "https://bauerip.com/services/european-patent-attorney",
      },
      {
        rel: "alternate",
        hrefLang: "en-US",
        href: "https://bauerip.com/services/european-patent-attorney",
      },
      {
        rel: "alternate",
        hrefLang: "en",
        href: "https://bauerip.com/services/european-patent-attorney",
      },
      {
        rel: "alternate",
        hrefLang: "x-default",
        href: "https://bauerip.com/services/european-patent-attorney",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "Bauer IP — European Patent Attorney",
          url: "https://bauerip.com/services/european-patent-attorney",
          areaServed: ["United States", "EP", "EU", "UPC"],
          provider: {
            "@type": "Person",
            name: "Karel Bauer",
            jobTitle: "European Patent Attorney",
            sameAs: [LINKEDIN_URL, EPO_OJ_URL],
          },
          serviceType: [
            "European patent filing",
            "EPO prosecution",
            "EPO opposition",
            "Unified Patent Court representation",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Karel Bauer",
          jobTitle: "European Patent Attorney",
          url: "https://bauerip.com/services/european-patent-attorney",
          image: "https://bauerip.com/karel-bauer-new.webp",
          sameAs: [LINKEDIN_URL, EPO_OJ_URL],
          knowsAbout: [
            "European patent prosecution",
            "EPO opposition",
            "PCT regional phase entry",
            "Unified Patent Court",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: PillarPage,
});

const whenToHire = [
  "You are preparing to enter Europe through PCT and need a real EPO strategy — not a copy of your US file.",
  "You've received an EPO examination report and the objections are getting sharper.",
  "A competitor's European patent is blocking you, and the 9-month opposition window is still open.",
  "You need to validate a granted European patent across multiple states — including the Unitary Patent option.",
  "You're weighing UPC opt-in vs opt-out for an existing portfolio.",
];

const services = [
  {
    title: "EPO filing & PCT regional phase",
    body: "Claim strategy adapted for European examination from the start — not translated at the last minute.",
  },
  {
    title: "European prosecution",
    body: "Direct handling of examination reports, amendments and oral proceedings before the EPO.",
  },
  {
    title: "EPO opposition (attack & defence)",
    body: "Central challenges within 9 months of grant — often the most cost-effective way to clear a blocking patent.",
  },
  {
    title: "UPC representation",
    body: "Representation before the Unified Patent Court, plus opt-in / opt-out strategy for existing EP portfolios.",
  },
  {
    title: "European validation & Unitary Patent",
    body: "Post-grant validation strategy across national offices and the Unitary Patent — cost-optimised for your markets.",
  },
  {
    title: "Freedom-to-operate & portfolio review",
    body: "Practical FTO opinions and portfolio triage for founders, CTOs and in-house IP counsel.",
  },
];

const posts = [
  {
    slug: "epo-vs-uspto-patent-practice-guide",
    title: "EPO vs USPTO: How European Patent Practice Differs",
    excerpt:
      "Claim interpretation, inventive step, no discovery, no damages — what changes when your case moves from the USPTO to the EPO.",
  },
  {
    slug: "epo-opposition-what-us-patent-owner-needs-to-know",
    title: "EPO Opposition: What a US Patent Owner Needs to Know",
    excerpt:
      "The 9-month window, the steps, the costs and the strategy — whether you are defending your EP patent or clearing a competitor's.",
  },
  {
    slug: "how-to-choose-a-european-patent-attorney",
    title: "How to Choose a European Patent Attorney (2026 Guide)",
    excerpt:
      "What actually matters when picking European counsel — qualifications, technical fit, UPC eligibility.",
  },
  {
    slug: "european-patent-vs-unitary-patent",
    title: "European Patent vs Unitary Patent: Which Should You File?",
    excerpt:
      "Classic EP validation, Unitary Patent, or both — a decision framework by market and budget.",
  },
  {
    slug: "cost-of-filing-a-european-patent",
    title: "European Patent Cost 2026: EPO vs Unitary Patent Fees",
    excerpt:
      "Official fees, attorney fees, renewals and three worked cost examples from filing to grant.",
  },
  {
    slug: "freedom-to-operate-europe-us-guide",
    title: "Freedom to Operate (FTO) in Europe: Why, When and How Much (2026)",
    excerpt:
      "What an FTO study covers, why US companies need it before launching in Europe, and cost ranges for patent, trademark and design clearance.",
  },

  {
    slug: "does-us-patent-protect-europe",
    title: "Does a US Patent Protect You in Europe?",
    excerpt:
      "No — and here are the filing deadlines, EPO vs USPTO differences and costs that follow from that.",
  },

  {
    slug: "patent-attorney-vs-patent-lawyer",
    title: "Patent Attorney vs Patent Lawyer: What EU Inventors Need to Know",
    excerpt:
      "Who can represent you before the EPO, national courts, and the UPC — and when each matters.",
  },
  {
    slug: "epo-filing-process-step-by-step",
    title: "EPO Filing Process Explained Step by Step",
    excerpt:
      "From priority to grant: search, examination, oral proceedings and validation.",
  },
  {
    slug: "validating-a-european-patent",
    title: "Validating a European Patent in Germany, France & the UK",
    excerpt:
      "National validation requirements, translation rules and cost-cutting strategies after grant.",
  },
  {
    slug: "file-european-patent-from-us",
    title: "How US Startups File a European Patent from the US",
    excerpt:
      "PCT vs direct EP, priority timing, and how to prepare a US-drafted application for European examination.",
  },
  {
    slug: "when-to-hire-a-european-patent-attorney",
    title: "When to Hire a European Patent Attorney: 7 Signs You're Ready",
    excerpt:
      "Practical triggers — PCT deadlines, examination reports, opposition windows and UPC decisions.",
  },
];

const faqs = [
  {
    q: "What is a European Patent Attorney?",
    a: "A European Patent Attorney is a professional entered on the list maintained by the European Patent Office, entitled to represent applicants before the EPO in filing, prosecution and opposition proceedings. Many are also representatives before the Unified Patent Court.",
  },
  {
    q: "Do I need a European Patent Attorney if I already have US counsel?",
    a: "Yes. Only European Patent Attorneys can represent applicants before the EPO. European examination and claim practice differ substantially from the US, so having dedicated European counsel from the start protects scope and avoids late, expensive rework.",
  },
  {
    q: "Can you represent us before the Unified Patent Court?",
    a: "I am entered as a UPC representative, supported by a CEIPI diploma in Patent Litigation and the European Patent Litigation Certificate from ERA and Maastricht University. This is a professional qualification; it is stated separately from my EPO prosecution track record.",
  },
  {
    q: "How much does a European Patent Attorney cost?",
    a: "Fees depend on the stage — filing, examination, opposition or UPC litigation. For a specific matter I quote transparently; see the cost breakdown post below for typical ranges.",
  },
  {
    q: "Do you work with US companies directly?",
    a: "Yes. US companies, in-house counsel and US patent firms can instruct me directly for PCT regional-phase entry, direct EP filings and EPO prosecution.",
  },
  {
    q: "Can a US patent attorney file at the EPO for us instead?",
    a: "No. Only a qualified European Patent Attorney (or equivalent professional representative on the EPO list) can represent an applicant before the EPO. US firms typically instruct a European attorney as foreign associate — you can also engage one directly and remove the extra layer.",
  },
  {
    q: "How do European patent costs compare for US applicants?",
    a: "For a typical PCT regional phase entry, expect official EPO fees plus attorney work through examination, followed by validation and renewals. The total depends on claim count, examination complexity and selected validation states; see the stage-by-stage guide below.",
  },
  {
    q: "How do time zones and communication work?",
    a: "I schedule calls in US business hours (typically morning ET / afternoon CET) and respond to email within one business day. EPO deadlines are handled on European time so nothing slips.",
  },
  {
    q: "Which technology fields do you handle?",
    a: "Physics-based and engineering subject matter: photonics and optics, semiconductors and electronics, measurement and sensor technology, materials, mechanical engineering, medical devices and software-implemented inventions with a technical effect.",
  },
  {
    q: "How long does a European patent take from filing to grant?",
    a: "Typically three to five years from the EP filing or regional-phase entry date, depending on the technical field and how many examination rounds are needed. PACE accelerated examination can shorten this significantly where a business reason exists.",
  },
  {
    q: "Should our US company opt out of the UPC?",
    a: "It depends on portfolio value, litigation risk appetite, and where your competitors sit. Opt-out avoids central revocation but forfeits central enforcement. I review existing EP portfolios and recommend opt-in / opt-out per family — see the UPC opt-out guide for the framework.",
  },
];


function PillarPage() {
  const consultBody =
    "Hello Karel,\n\nI'd like to speak with a European Patent Attorney about the following matter.\n\n— Name:\n— Company:\n— Technology field:\n— Stage (filing, prosecution, opposition, UPC):\n— Deadline (if any):\n— Brief description:\n";

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        {/* HERO */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-6xl px-6 pt-20 md:pt-28 pb-16 md:pb-20">
            <p className="eyebrow mb-6">
              For US Companies · EPO &amp; UPC Representative
            </p>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-4xl">
              <span className="italic text-accent-text">
                European Patent Attorney
              </span>{" "}
              for US Companies
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-muted-foreground leading-relaxed">
              More than 50 EPO prosecution matters over 10+ years, handled
              directly by an EQE-qualified European Patent Attorney with a
              technical focus in physics, electronics and ICT.
            </p>


            <div className="mt-10 flex flex-wrap gap-3">
              <CTAButton
                variant="accent"
                href={mailto({
                  subject: "Speak with a European Patent Attorney",
                  body: consultBody,
                })}
              >
                Book a Consultation
              </CTAButton>
              <CTAButton
                variant="secondary"
                href={mailto({
                  subject: "Review my EPO matter",
                  body: consultBody,
                })}
              >
                Send Your Matter for Review
              </CTAButton>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Response within one business day · No obligation
            </p>
          </div>
        </section>

        {/* CREDENTIALS / ABOUT */}
        <div className="border-b border-border bg-secondary/30">
          <Section
            eyebrow="About Karel Bauer"
            title="Who you are actually hiring."
            intro="Karel Bauer combines a verified EPO prosecution track record with technical training and a separately stated qualification to represent before the UPC."
          >
            <div className="grid gap-10 md:grid-cols-[220px_1fr] items-start">
              <img
                src={portrait.url}
                alt="Karel Bauer, European Patent Attorney"
                className="w-40 md:w-full rounded-md border border-border"
                width={220}
                height={306}
              />
              <div>
                <dl className="grid gap-px md:grid-cols-3 bg-border border border-border">
                  {[
                    {
                      t: "EPO track record",
                      d: "More than 50 EPO prosecution matters handled across more than 10 years of European patent practice.",
                    },
                    {
                      t: "Qualification & technology",
                      d: "European Qualifying Examination (EQE); physics background with a focus on physics, electronics and ICT.",
                    },
                    {
                      t: "UPC qualification",
                      d: "UPC representative, supported by a CEIPI diploma in Patent Litigation and the European Patent Litigation Certificate from ERA and Maastricht University.",
                    },
                  ].map((c) => (
                    <div key={c.t} className="bg-background p-8">
                      <dt className="font-serif text-xl text-foreground">{c.t}</dt>
                      <dd className="mt-3 text-muted-foreground leading-relaxed">
                        {c.d}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-6 text-sm text-muted-foreground">
                  Verifiable credentials:{" "}
                  <a
                    href={EPO_OJ_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-text underline underline-offset-4"
                  >
                    EPO Official Journal entry
                  </a>{" "}
                  ·{" "}
                  <a
                    href={LINKEDIN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-text underline underline-offset-4"
                  >
                    LinkedIn profile
                  </a>
                </p>
              </div>
            </div>
          </Section>
        </div>



        {/* US-FOCUSED INTRO */}
        <Section
          eyebrow="Built for US clients"
          title="Why US companies work with a European Patent Attorney directly."
        >
          <div className="grid gap-10 md:grid-cols-[1.2fr_1fr] items-start">
            <div className="space-y-5 text-lg text-muted-foreground leading-relaxed max-w-2xl">
              <p>
                European prosecution is not a translation exercise. Added
                matter, problem-solution analysis, technical effect and the
                structure of allowable claim amendments require an EPO-specific
                strategy from the outset.
              </p>
              <p>
                Working directly means the same attorney reviews your US
                specification, analyses the claims, speaks with your technical
                team and prepares the EPO response. The reasoning is not passed
                from a relationship partner to a separate drafter.
              </p>
              <p>
                US in-house counsel, general counsel and founders typically
                bring me in for PCT regional phase entry, tough EPO office
                actions, opposition against a blocking European patent, and
                UPC opt-in / opt-out decisions.
              </p>
              <p>
                How European practice actually differs from what your US firm
                is used to — examination style, claim interpretation, no
                discovery, no damages before the EPO — is covered in the{" "}
                <Link
                  to="/blog/$slug"
                  params={{ slug: "epo-vs-uspto-patent-practice-guide" }}
                  className="text-accent-text underline underline-offset-4"
                >
                  EPO vs USPTO practice guide
                </Link>
                .
              </p>
            </div>
            <ul className="grid gap-px bg-border border border-border">
              {[
                "50+ EPO prosecution matters over more than 10 years",
                "One attorney analyses the claims and prepares the response",
                "EQE-qualified, with a physics, electronics and ICT focus",
                "US business-hour overlap for calls and deadlines",
                "UPC qualification stated separately from prosecution experience",
              ].map((item) => (
                <li
                  key={item}
                  className="bg-background p-5 text-foreground leading-relaxed"
                >
                  <span className="text-accent-text mr-3">→</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Section>

        {/* WHAT IS A EUROPEAN PATENT ATTORNEY */}
        <div className="border-t border-border bg-secondary/30">
          <Section
            eyebrow="Understanding the role"
            title="What is a European Patent Attorney (and how they differ from a US patent attorney)"
          >
            <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] items-start">
              <div className="space-y-5 text-lg text-muted-foreground leading-relaxed">
                <p>
                  A <strong>European Patent Attorney</strong> is a professional
                  representative entered on the official list maintained by the
                  European Patent Office. That registration — gained after the
                  European Qualifying Examination (EQE) — gives the right to
                  represent applicants directly before the EPO in filing,
                  examination, opposition and appeal proceedings.
                </p>
                <p>
                  It is a regulated title, not a generic job description. In
                  everyday language people sometimes say “patent lawyer” or
                  “patent counsel”, but the EPO-registered professional who can
                  actually act for you at the EPO is a{" "}
                  <em>European Patent Attorney</em>.
                </p>
                <p>
                  UPC representation is a separate post-grant qualification.
                  I am a registered UPC representative on the basis of advanced
                  patent-litigation training; this should not be confused with
                  the EPO prosecution track record described on this page.
                </p>
                <p className="text-base">
                  More on the distinction:{" "}
                  <Link
                    to="/blog/$slug"
                    params={{ slug: "patent-attorney-vs-patent-lawyer" }}
                    className="text-accent-text underline underline-offset-4"
                  >
                    Patent attorney vs patent lawyer: what EU inventors need to
                    know
                  </Link>
                  .
                </p>
              </div>
              <div className="overflow-x-auto border border-border">
                <table className="w-full text-left text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="p-4 font-medium text-foreground">Topic</th>
                      <th className="p-4 font-medium text-foreground">
                        European Patent Attorney
                      </th>
                      <th className="p-4 font-medium text-foreground">
                        US patent attorney
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      [
                        "Registration",
                        "EPO list of professional representatives (EQE)",
                        "USPTO registered practitioner",
                      ],
                      [
                        "Where they can act",
                         "EPO and national EP validations; UPC where separately qualified",
                        "USPTO only (not EPO)",
                      ],
                      [
                        "Typical role",
                        "Draft EP claims, respond to EPO office actions, conduct oppositions and UPC cases",
                        "Draft US applications, respond to USPTO actions, US litigation support",
                      ],
                      [
                        "How they usually work with US firms",
                        "Engaged directly by US companies or instructed as foreign associate by US counsel",
                        "Often instructs European counsel for EP/UPC matters",
                      ],
                    ].map(([topic, epa, us]) => (
                      <tr key={topic} className="border-t border-border">
                        <td className="p-4 font-medium text-foreground">
                          {topic}
                        </td>
                        <td className="p-4 text-muted-foreground">{epa}</td>
                        <td className="p-4 text-muted-foreground">{us}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>
        </div>

        {/* WHEN TO HIRE */}
        <Section
          eyebrow="When to hire European counsel"
          title="Situations where a European Patent Attorney pays for itself."
        >
          <ul className="grid gap-px md:grid-cols-2 bg-border">
            {whenToHire.map((item, i) => (
              <li
                key={i}
                className="bg-background p-8 flex gap-5 text-foreground"
              >
                <span className="font-serif text-accent-text text-xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* DEADLINES & TIMING */}
        <div className="border-t border-border">
          <Section
            eyebrow="Deadlines & timing"
            title="The deadlines that decide whether you still have options."
            intro="European patent practice runs on hard deadlines. These are the ones US clients most often need to calendar — several of them cannot be extended."
          >
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="p-4 font-medium text-foreground">Milestone</th>
                    <th className="p-4 font-medium text-foreground">Deadline</th>
                    <th className="p-4 font-medium text-foreground">Extension possible?</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["PCT regional-phase entry before the EPO", "31 months from the priority date", "No — a missed entry ends the PCT route in Europe"],
                    ["Response to an EPO examination report", "Typically 4 months from notification", "Yes — usually extendable on request"],
                    ["Opposition against a granted EP patent", "9 months from publication of the mention of grant", "No — strictly enforced"],
                    ["Response to an opposition (patentee)", "4 months from notification", "Yes — on request, within limits"],
                    ["Request for Unitary Patent effect", "1 month from publication of the mention of grant", "No — strictly enforced"],
                    ["Appeal to the EPO Boards of Appeal", "2 months to file, 4 months for grounds", "No"],
                    ["My reply to your enquiry or matter review", "One business day", "—"],
                  ].map(([m, d, e]) => (
                    <tr key={m} className="border-t border-border">
                      <td className="p-4 font-medium text-foreground">{m}</td>
                      <td className="p-4 text-muted-foreground">{d}</td>
                      <td className="p-4 text-muted-foreground">{e}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-3xl">
              Facing the 9-month opposition window as a patent owner — on
              either side?{" "}
              <Link
                to="/blog/$slug"
                params={{ slug: "epo-opposition-what-us-patent-owner-needs-to-know" }}
                className="text-accent-text underline underline-offset-4"
              >
                EPO opposition: what a US patent owner needs to know
              </Link>{" "}
              walks through the timeline, the steps and the costs.
            </p>
          </Section>
        </div>

        {/* SERVICES */}
        <div className="border-t border-border bg-secondary/30">
          <Section
            eyebrow="Services"
            title="What a European Patent Attorney does for you."
          >
            <div className="grid gap-px md:grid-cols-2 bg-border">
              {services.map((s) => (
                <div key={s.title} className="bg-background p-8">
                  <h3 className="font-serif text-xl text-foreground leading-snug">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">
                    {s.body}
                  </p>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* SCOPE / PROCESS / FEES — engagement specifics */}
        <Section
          eyebrow="Scope, process & fees"
          title="How an engagement with me actually runs."
          intro="This is the engagement side of the practice — what I take on, how a matter moves, and what each stage costs. The technical overview of the European patent system lives on the European patents page."
        >
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <h3 className="font-serif text-2xl text-foreground leading-snug">
                What I take on — and what I don&rsquo;t
              </h3>
              <ul className="mt-6 grid gap-px bg-border border border-border">
                {[
                  "EPO work: direct EP filings, PCT regional-phase entry, examination, oral proceedings, opposition and appeal.",
                  "Post-grant: Unitary Patent requests, validation coordination, renewals oversight, UPC opt-out filings.",
                  "Advisory: freedom-to-operate reads on European rights, pre-filing review of US specifications, portfolio pruning.",
                  "Not taken on: US prosecution, litigation before national civil courts other than UPC representation, and trade secret or contract disputes.",
                ].map((item) => (
                  <li
                    key={item}
                    className="bg-background p-5 text-foreground leading-relaxed"
                  >
                    <span className="text-accent-text mr-3">→</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-serif text-2xl text-foreground leading-snug">
                The process, step by step
              </h3>
              <ol className="mt-6 space-y-0">
                {[
                  {
                    t: "1. Intake call (30 minutes, no charge)",
                    d: "You describe the matter and deadlines; I confirm whether European rights are still available and what the clock looks like.",
                  },
                  {
                    t: "2. Written scope and fee estimate",
                    d: "Within one business day you get a stage-by-stage estimate — official fees separated from attorney fees — plus a conflict check.",
                  },
                  {
                    t: "3. Filing or entry",
                    d: "I review the US specification for European fallback positions before filing, then file or enter the regional phase and confirm the receipt to your docket.",
                  },
                  {
                    t: "4. Prosecution",
                    d: "Each EPO communication is reported in a short English memo with a recommendation, a cost, and a deadline — not a forwarded PDF.",
                  },
                  {
                    t: "5. Grant, validation and renewals",
                    d: "We decide Unitary Patent versus national validation on your commercial footprint, and set the renewal and UPC opt-out plan.",
                  },
                ].map((s) => (
                  <li
                    key={s.t}
                    className="border-t border-border py-5 last:border-b"
                  >
                    <p className="font-medium text-foreground">{s.t}</p>
                    <p className="mt-2 text-muted-foreground leading-relaxed">
                      {s.d}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="mt-14">
            <h3 className="font-serif text-2xl text-foreground leading-snug">
              Indicative attorney fees by stage
            </h3>
            <p className="mt-3 text-muted-foreground leading-relaxed max-w-3xl">
              Ranges below are attorney fees only, in EUR, excluding official
              EPO fees. Every matter is quoted in writing before work starts.
            </p>
            <div className="mt-6 overflow-x-auto border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="p-4 font-medium text-foreground">Stage</th>
                    <th className="p-4 font-medium text-foreground">
                      Indicative attorney fee
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["PCT regional-phase entry / direct EP filing", "€2,500 – €5,000"],
                    ["Pre-filing review of a US specification", "€900 – €1,800"],
                    ["Response to an EPO examination report", "€1,500 – €4,000 per round"],
                    ["Oral proceedings (examination)", "€6,000 – €12,000"],
                    ["EPO opposition to first-instance decision", "€15,000 – €40,000"],
                    ["Unitary Patent request / validation coordination", "€700 – €2,500"],
                    ["UPC opt-out filing (per family)", "€250 – €600"],
                  ].map(([stage, fee]) => (
                    <tr key={stage} className="border-t border-border">
                      <td className="p-4 text-foreground">{stage}</td>
                      <td className="p-4 text-muted-foreground">{fee}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Official EPO fees are set out separately in the{" "}
              <Link
                to="/blog/$slug"
                params={{ slug: "cost-of-filing-a-european-patent" }}
                className="text-accent-text underline underline-offset-4"
              >
                European patent cost guide
              </Link>{" "}
              and modelled year by year in the{" "}
              <Link
                to="/tools/epo-renewal-fees"
                className="text-accent-text underline underline-offset-4"
              >
                EPO renewal fees hub
              </Link>
              .
            </p>
          </div>
        </Section>


        {/* TECHNOLOGY FIELDS */}
        <Section
          eyebrow="Technology fields"
          title="Physics, electronics and ICT matters handled personally."
          intro="A physics background means the technical argument in an EPO examination report is answered directly, without a translation layer between your engineers and your European attorney."
        >
          <div className="grid gap-px bg-border border border-border md:grid-cols-3">
            {[
              {
                t: "Photonics & optics",
                d: "Lasers, imaging systems, optical metrology, fibre and integrated optics.",
              },
              {
                t: "Semiconductors & electronics",
                d: "Device structures, fabrication processes, circuits and power electronics.",
              },
              {
                t: "Sensors & measurement",
                d: "Sensing principles, calibration methods and instrumentation.",
              },
              {
                t: "Materials & chemistry-adjacent tech",
                d: "Coatings, composites, thin films and materials processing.",
              },
              {
                t: "Mechanical & medical devices",
                d: "Precision mechanics, manufacturing equipment and device engineering.",
              },
              {
                t: "Software with technical effect",
                d: "Control, signal processing and computer-implemented inventions under EPO Art. 52 practice.",
              },
            ].map((f) => (
              <div key={f.t} className="bg-background p-8">
                <h3 className="font-serif text-xl text-foreground leading-snug">
                  {f.t}
                </h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  {f.d}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground max-w-3xl">
            Handling an examination report right now? See{" "}
            <Link
              to="/services/epo-office-action-response"
              className="text-accent-text underline underline-offset-4"
            >
              EPO office action response for US applicants
            </Link>
            .
          </p>
        </Section>

        {/* CREDENTIALS / WHY ME */}
        <div className="border-y border-border bg-primary text-primary-foreground">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24 grid gap-12 md:grid-cols-[1fr_1.4fr]">
            <div>
              <p className="eyebrow mb-4" style={{ color: "var(--accent)" }}>
                Credentials
              </p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                Qualified. Technical. Direct.
              </h2>
            </div>
            <ul className="grid gap-3 text-base text-primary-foreground/90">
              <li className="border-t border-primary-foreground/15 pt-3">
                <span className="text-accent mr-3">—</span> 50+ EPO prosecution
                matters across more than 10 years
              </li>
              <li className="border-t border-primary-foreground/15 pt-3">
                <span className="text-accent mr-3">—</span> European Patent
                Attorney qualified through the EQE
              </li>
              <li className="border-t border-primary-foreground/15 pt-3">
                <span className="text-accent mr-3">—</span> Physics, electronics
                and ICT technical focus
              </li>
              <li className="border-t border-primary-foreground/15 pt-3">
                <span className="text-accent mr-3">—</span> UPC representative:
                CEIPI Patent Litigation diploma and ERA/Maastricht certificate
              </li>
              <li className="border-t border-primary-foreground/15 pt-3">
                <span className="text-accent mr-3">—</span> Direct
                communication with inventors, engineers and founders
              </li>
            </ul>
          </div>
        </div>

        {/* LEARN MORE — internal links to the 8 posts */}
        <Section
          eyebrow="Learn more"
          title="Guides on European patents & EPO practice."
        >
          <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl mb-10">
            In-depth articles covering the decisions you'll face — from
            choosing counsel and filing costs to validation, UPC and hiring
            triggers.
          </p>
          <ul className="grid gap-px md:grid-cols-2 bg-border">
            {posts.map((p) => (
              <li key={p.slug} className="bg-background">
                <Link
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="block p-8 hover:bg-secondary/40 transition-colors group"
                >
                  <h3 className="font-serif text-xl text-foreground leading-snug group-hover:text-accent-text">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">
                    {p.excerpt}
                  </p>
                  <span className="mt-4 inline-block text-accent-text text-sm">
                    Read the guide →
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <p className="mt-10 text-muted-foreground leading-relaxed max-w-3xl">
            Free tools and single-issue pages:{" "}
            <Link to="/tools/epo-renewal-fees" className="underline underline-offset-4">
              EPO renewal fees and annuity calculator
            </Link>
            ,{" "}
            <Link
              to="/tools/european-patent-validation-costs"
              className="underline underline-offset-4"
            >
              European patent validation costs by country
            </Link>{" "}
            and{" "}
            <Link
              to="/services/epo-office-action-response"
              className="underline underline-offset-4"
            >
              responding to an EPO office action
            </Link>
            .
          </p>
        </Section>


        {/* FAQ */}
        <div className="border-t border-border bg-secondary/30">
          <Section
            eyebrow="FAQ"
            title="European Patent Attorney — common questions."
          >
            <dl className="divide-y divide-border border-y border-border">
              {faqs.map((f) => (
                <div
                  key={f.q}
                  className="grid md:grid-cols-[1fr_2fr] gap-6 py-8"
                >
                  <dt className="font-serif text-lg text-foreground">{f.q}</dt>
                  <dd className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </dd>
                </div>
              ))}
            </dl>
          </Section>
        </div>

        {/* FINAL CTA */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
            <h2 className="font-serif text-3xl md:text-5xl text-foreground leading-tight max-w-3xl">
              Ready to speak with a European Patent Attorney?
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              Send me the basics of your matter — filing plan, examination
              report, opposition notice or UPC question — and I'll come back
              within one business day.
            </p>
            <div className="mt-10 grid gap-10 lg:grid-cols-[1.1fr_1fr] items-start">
              <ConsultationWizard matter="patent" context="patent_pillar_final_cta" />
              <div>
                <p className="text-sm text-muted-foreground mb-4">
                  Prefer to just email?
                </p>
                <div className="flex flex-wrap gap-3">
                  <CTAButton
                    variant="secondary"
                    href={mailto({
                      subject: "Review my EPO matter",
                      body: consultBody,
                    })}
                    trackLabel="patent_pillar_email_direct"
                  >
                    Send Your Matter for Review
                  </CTAButton>
                </div>
              </div>
            </div>

            <p className="mt-8 text-sm text-muted-foreground">
              Prefer an overview first?{" "}
              <Link
                to="/european-patents"
                className="underline hover:text-accent-text"
              >
                See the European patents service page
              </Link>
              . Instructing on behalf of a client as a US firm?{" "}
              <Link
                to="/services/foreign-associate-for-us-firms"
                className="underline hover:text-accent-text"
              >
                See foreign associate services for US firms
              </Link>
              .
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
